import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './DeliveryRuns.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const STATUS_LABELS = {
  draft:       'Draft',
  published:   'Published',
  in_progress: 'In Progress',
  completed:   'Completed',
};
const STOP_STATUS_LABELS = { pending: 'Pending', done: 'Done', failed: 'Failed', skipped: 'Skipped' };
const CONSOLIDATION_LABELS = {
  not_needed: null,
  pending:    '⚠ Stock transfer required before dispatch',
  done:       '✓ Consolidation complete',
  skipped:    'Transfer skipped — driver will collect from secondary warehouse',
};

export default function DeliveryRuns() {
  const { user } = useContext(AuthContext);
  const headers  = { Authorization: `Bearer ${user?.token}` };

  const todayStr = new Date().toISOString().split('T')[0];
  const [date,       setDate]       = useState(todayStr);
  const [numDrivers, setNumDrivers] = useState(5);
  const [runs,       setRuns]       = useState([]);
  const [drivers,    setDrivers]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  // Expanded run stops
  const [expandedRun,   setExpandedRun]   = useState(null);
  const [stops,         setStops]         = useState([]);
  const [stopsLoading,  setStopsLoading]  = useState(false);

  // Picklist for warehouse stops
  const [expandedPicklist, setExpandedPicklist] = useState(null); // stopId
  const [picklists,        setPicklists]        = useState({});   // { stopId: items[] }

  // Geocode health panel
  const [showHealth,     setShowHealth]     = useState(false);
  const [health,         setHealth]         = useState(null);
  const [healthLoading,  setHealthLoading]  = useState(false);

  // Ungeocoded orders
  const [ungeocoded, setUngeocoded] = useState([]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [runsRes, driversRes] = await Promise.all([
        axios.get(`${API_URL}/delivery-runs`,         { headers, params: { date } }),
        axios.get(`${API_URL}/delivery-runs/drivers`, { headers }),
      ]);
      setRuns(runsRes.data);
      setDrivers(driversRes.data);
    } catch {
      setError('Failed to load delivery runs');
    } finally {
      setLoading(false);
    }
  }, [date]); // eslint-disable-line

  const fetchUngeocoded = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/delivery-runs/ungeocoded-orders`, { headers, params: { date } });
      setUngeocoded(r.data);
    } catch { setUngeocoded([]); }
  }, [date]); // eslint-disable-line

  useEffect(() => {
    fetchRuns();
    fetchUngeocoded();
  }, [fetchRuns, fetchUngeocoded]);

  const handleGenerate = async () => {
    if (!window.confirm(
      `Generate delivery runs for ${date} with up to ${numDrivers} drivers?\nExisting draft runs for this date will be replaced.`
    )) return;
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      const r = await axios.post(`${API_URL}/delivery-runs/generate`, { date, num_drivers: numDrivers }, { headers });
      setSuccess(`Generated ${r.data.runs_created} run(s)`);
      await fetchRuns();
      await fetchUngeocoded();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate runs');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateRun = async (runId, patch) => {
    try {
      await axios.put(`${API_URL}/delivery-runs/${runId}`, patch, { headers });
      setRuns(prev => prev.map(r => r.id === runId ? { ...r, ...patch } : r));
    } catch {
      setError('Failed to update run');
    }
  };

  const handleDeleteRun = async (runId) => {
    if (!window.confirm('Delete this draft run?')) return;
    try {
      await axios.delete(`${API_URL}/delivery-runs/${runId}`, { headers });
      setRuns(prev => prev.filter(r => r.id !== runId));
      if (expandedRun === runId) setExpandedRun(null);
    } catch {
      setError('Failed to delete run');
    }
  };

  const toggleStops = async (runId) => {
    if (expandedRun === runId) { setExpandedRun(null); setExpandedPicklist(null); return; }
    setExpandedRun(runId);
    setStopsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/delivery-runs/${runId}/stops`, { headers });
      setStops(r.data);
    } catch {
      setStops([]);
    } finally {
      setStopsLoading(false);
    }
  };

  const togglePicklist = async (runId, stopId) => {
    if (expandedPicklist === stopId) { setExpandedPicklist(null); return; }
    setExpandedPicklist(stopId);
    if (picklists[stopId]) return;
    try {
      const r = await axios.get(`${API_URL}/delivery-runs/${runId}/stops/${stopId}/picklist`, { headers });
      setPicklists(prev => ({ ...prev, [stopId]: r.data }));
    } catch {
      setPicklists(prev => ({ ...prev, [stopId]: [] }));
    }
  };

  const handleStopStatus = async (runId, stopId, status) => {
    try {
      await axios.patch(`${API_URL}/delivery-runs/${runId}/stops/${stopId}`, { status }, { headers });
      setStops(prev => prev.map(s => s.id === stopId ? { ...s, status } : s));
    } catch {
      setError('Failed to update stop');
    }
  };

  const handlePublishAll = async () => {
    const drafts = runs.filter(r => r.status === 'draft');
    if (drafts.length === 0) return;
    try {
      await Promise.all(
        drafts.map(r => axios.put(`${API_URL}/delivery-runs/${r.id}`, { status: 'published' }, { headers }))
      );
      setRuns(prev => prev.map(r => r.status === 'draft' ? { ...r, status: 'published' } : r));
      setSuccess('All draft runs published');
    } catch {
      setError('Failed to publish runs');
    }
  };

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const r = await axios.get(`${API_URL}/delivery-runs/geocode-health`, { headers });
      setHealth(r.data);
    } catch { setHealth(null); }
    finally { setHealthLoading(false); }
  };

  const toggleHealth = () => {
    setShowHealth(v => !v);
    if (!showHealth && !health) fetchHealth();
  };

  const handleRetryGeocode = async (addressId) => {
    try {
      await axios.post(`${API_URL}/delivery-runs/geocode-retry/${addressId}`, {}, { headers });
      fetchHealth();
      fetchUngeocoded();
    } catch {
      setError('Failed to queue retry');
    }
  };

  const hasDrafts = runs.some(r => r.status === 'draft');

  return (
    <div className="dr-page">
      <div className="dr-header">
        <div>
          <h2>Delivery Runs</h2>
          <p>Generate and manage daily delivery routes</p>
        </div>
        <button className="dr-btn-health" onClick={toggleHealth}>
          {showHealth ? 'Hide' : 'Geocode Health'}
        </button>
      </div>

      {error   && <div className="dr-error"   onClick={() => setError('')}>{error}</div>}
      {success && <div className="dr-success" onClick={() => setSuccess('')}>{success}</div>}

      {/* Controls */}
      <div className="dr-controls">
        <label className="dr-label">
          Date
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="dr-input" />
        </label>
        <label className="dr-label">
          Drivers
          <input type="number" value={numDrivers} min={1} max={20}
            onChange={e => setNumDrivers(parseInt(e.target.value, 10) || 1)}
            className="dr-input dr-input-sm" />
        </label>
        <button className="dr-btn-generate" onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating…' : 'Generate Runs'}
        </button>
        {hasDrafts && (
          <button className="dr-btn-publish" onClick={handlePublishAll}>
            Publish All
          </button>
        )}
      </div>

      {/* Ungeocoded warning */}
      {ungeocoded.length > 0 && (
        <div className="dr-warning">
          <strong>{ungeocoded.length} order(s) excluded</strong> — address not geocoded:&nbsp;
          {ungeocoded.map(o => (
            <span key={o.order_id} className="dr-warning-order">{o.order_no} ({o.customer_name})</span>
          ))}
        </div>
      )}

      {/* Geocode health panel */}
      {showHealth && (
        <div className="dr-health-panel">
          <h3>Geocode Health</h3>
          {healthLoading ? <p>Loading…</p> : health ? (
            <>
              <div className="dr-health-summary">
                {health.summary.map(s => (
                  <span key={s.geocode_status} className={`dr-geocode-badge dr-geocode-${s.geocode_status}`}>
                    {s.geocode_status}: {s.count}
                  </span>
                ))}
              </div>
              {health.failed_addresses.length > 0 && (
                <table className="dr-table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr><th>Address</th><th>Error</th><th>Customers</th><th></th></tr>
                  </thead>
                  <tbody>
                    {health.failed_addresses.map(a => (
                      <tr key={a.id}>
                        <td>{a.address}</td>
                        <td><span className="dr-error-text">{a.geocode_error}</span></td>
                        <td>{a.customer_count}</td>
                        <td>
                          <button className="dr-btn-sm" onClick={() => handleRetryGeocode(a.id)}>Retry</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : <p className="dr-muted">No data</p>}
        </div>
      )}

      {/* Runs list */}
      {loading ? (
        <p className="dr-muted">Loading runs…</p>
      ) : runs.length === 0 ? (
        <p className="dr-muted">No runs found for {date}. Click Generate Runs to create them.</p>
      ) : (
        <div className="dr-runs-list">
          {runs.map(run => (
            <div key={run.id} className={`dr-run-card dr-run-${run.status}`}>
              <div className="dr-run-header">
                <div className="dr-run-title">
                  <span className="dr-run-label">{run.label}</span>
                  <span className={`dr-status-badge dr-status-${run.status}`}>
                    {STATUS_LABELS[run.status]}
                  </span>
                  {run.delivery_window && (
                    <span className="dr-window-badge">{run.delivery_window}</span>
                  )}
                  {run.warehouse_name && (
                    <span className="dr-warehouse-badge">🏭 {run.warehouse_name}</span>
                  )}
                </div>
                <div className="dr-run-meta">
                  <span>{run.stop_count} stop{run.stop_count !== 1 ? 's' : ''}</span>
                  {run.warehouse_stop_count > 0 && (
                    <span className="dr-meta-wh">+{run.warehouse_stop_count} wh pickup</span>
                  )}
                  {run.est_duration_min > 0 && <span>~{run.est_duration_min}m</span>}
                  {run.est_distance_km  > 0 && <span>{run.est_distance_km}km</span>}
                  <span className="dr-progress">✓{run.done_count || 0} ✗{run.failed_count || 0}</span>
                </div>
              </div>

              {/* Consolidation banner */}
              {run.consolidation_status && run.consolidation_status !== 'not_needed' && (
                <div className={`dr-consolidation dr-consolidation-${run.consolidation_status}`}>
                  <span>{CONSOLIDATION_LABELS[run.consolidation_status]}</span>
                  {run.consolidation_status === 'pending' && (
                    <button className="dr-btn-sm dr-btn-consolidation-done"
                      onClick={() => handleUpdateRun(run.id, { consolidation_status: 'done' })}>
                      Mark Done
                    </button>
                  )}
                </div>
              )}

              <div className="dr-run-actions">
                {/* Supply partner assignment */}
                <select
                  className="dr-select"
                  value={run.driver_id || ''}
                  onChange={e => handleUpdateRun(run.id, { driver_id: e.target.value || null })}
                  disabled={run.status === 'completed'}
                >
                  <option value="">Assign supply partner…</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>

                {/* Status transitions */}
                {run.status === 'draft' && (
                  <button className="dr-btn-sm dr-btn-publish-one"
                    onClick={() => handleUpdateRun(run.id, { status: 'published' })}>
                    Publish
                  </button>
                )}
                {run.status === 'published' && (
                  <button className="dr-btn-sm"
                    onClick={() => handleUpdateRun(run.id, { status: 'draft' })}>
                    Unpublish
                  </button>
                )}
                {run.status === 'in_progress' && (
                  <button className="dr-btn-sm dr-btn-complete"
                    onClick={() => handleUpdateRun(run.id, { status: 'completed' })}>
                    Mark Complete
                  </button>
                )}

                <button className="dr-btn-sm" onClick={() => toggleStops(run.id)}>
                  {expandedRun === run.id ? 'Hide Stops' : 'Stops'}
                </button>

                {run.status === 'draft' && (
                  <button className="dr-btn-sm dr-btn-delete" onClick={() => handleDeleteRun(run.id)}>
                    Delete
                  </button>
                )}
              </div>

              {/* Stops list */}
              {expandedRun === run.id && (
                <div className="dr-stops">
                  {stopsLoading ? (
                    <p className="dr-muted">Loading stops…</p>
                  ) : stops.length === 0 ? (
                    <p className="dr-muted">No stops</p>
                  ) : (
                    <table className="dr-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Type</th>
                          <th>Order / Warehouse</th>
                          <th>Address</th>
                          <th>ETA</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stops.map(stop => (
                          <React.Fragment key={stop.id}>
                            <tr className={`dr-stop-${stop.status} ${stop.stop_type === 'WAREHOUSE' ? 'dr-stop-warehouse' : ''}`}>
                              <td>{stop.sequence_no}</td>
                              <td>
                                {stop.stop_type === 'WAREHOUSE'
                                  ? <span className="dr-stop-type-badge dr-stop-type-wh">🏭 WH</span>
                                  : <span className="dr-stop-type-badge dr-stop-type-cust">📦</span>
                                }
                              </td>
                              <td>
                                {stop.stop_type === 'WAREHOUSE' ? (
                                  <span className="dr-wh-stop-name">
                                    {stop.warehouse_name}
                                    <button className="dr-btn-picklist"
                                      onClick={() => togglePicklist(run.id, stop.id)}>
                                      {expandedPicklist === stop.id ? '▲ Picklist' : '▼ Picklist'}
                                    </button>
                                  </span>
                                ) : (
                                  <span>
                                    <span className="dr-order-no">{stop.order_no}</span>
                                    <br />
                                    <span className="dr-customer-name">{stop.customer_name}</span>
                                  </span>
                                )}
                              </td>
                              <td className="dr-address">{stop.delivery_address}</td>
                              <td>{stop.est_arrival_time || '—'}</td>
                              <td>
                                {stop.stop_type === 'WAREHOUSE' ? (
                                  <span className={`dr-stop-badge dr-stop-badge-${stop.status}`}>
                                    {STOP_STATUS_LABELS[stop.status]}
                                  </span>
                                ) : (
                                  <select
                                    className="dr-select-sm"
                                    value={stop.status}
                                    onChange={e => handleStopStatus(run.id, stop.id, e.target.value)}
                                    disabled={run.status === 'draft'}
                                  >
                                    {Object.entries(STOP_STATUS_LABELS).map(([v, l]) => (
                                      <option key={v} value={v}>{l}</option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            </tr>

                            {/* Picklist row for warehouse stops */}
                            {stop.stop_type === 'WAREHOUSE' && expandedPicklist === stop.id && (
                              <tr className="dr-picklist-row">
                                <td colSpan={6}>
                                  <div className="dr-picklist">
                                    <strong>Items to collect from {stop.warehouse_name}:</strong>
                                    {picklists[stop.id] === undefined ? (
                                      <p className="dr-muted">Loading…</p>
                                    ) : picklists[stop.id].length === 0 ? (
                                      <p className="dr-muted">No items found</p>
                                    ) : (
                                      <table className="dr-table dr-table-picklist">
                                        <thead>
                                          <tr><th>Product</th><th>Qty</th></tr>
                                        </thead>
                                        <tbody>
                                          {picklists[stop.id].map((item, i) => (
                                            <tr key={i}>
                                              <td>{item.product_name}</td>
                                              <td>{item.total_qty}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
